"use client";

import {
  Livestream,
  getLivestreamById,
} from "@punchup/data/legacy/livestreaming";
import { SpecialRow, userHasPurchasedSpecial } from "@/data/specials";
import {
  getPublicVideoURL,
  getViewingTokenForSpecial,
} from "@punchup/video/client";
import { memo, useEffect, useMemo, useState } from "react";

import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { FeedPost } from "../types";
import VideoFeedCardHorizontal from "./VideoFeedCardHorizontal";
import VideoFeedCardSkeleton from "./VideoFeedCardSkeleton";
import VideoFeedCardVertical from "./VideoFeedCardVertical";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@/data/images";
import { getUserCanAccessContent } from "@punchup/data/legacy";
import { useFeatureGate } from "statsig-react";
import { useIsMobile } from "@/components/common/hooks";
import useSubscriptionPriceForComedian from "@/hooks/subscriptions/useSubscriptionPriceForComedian";
import { userIsSubscribedToComedian } from "@/data/subscriptions";
import { useToast } from "@/shadcn/ui/use-toast";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(advancedFormat);

interface Props {
  post: FeedPost;
  special: SpecialRow;
  comedian: Comedian;
  userFollowsComedian?: boolean;
  isLoggedOutState: boolean;
  muted: boolean;
  handleMutedChange: (value: boolean) => void;
  playing: boolean;
  screenIntersectionCallback: IntersectionObserverCallback;
  isMobileVideoLayoutEnabled?: boolean;
  hideEnabled?: boolean;
  onHide?: (postId: string) => void;
}

function VideoFeedCard({
  post,
  special,
  comedian,
  userFollowsComedian: userFollowsComedianParam = false,
  isLoggedOutState,
  muted,
  handleMutedChange,
  playing,
  screenIntersectionCallback,
  isMobileVideoLayoutEnabled,
  hideEnabled = false,
  onHide,
}: Props) {
  const { toast } = useToast();
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(true);
  const [profileImg, setProfileImg] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [userSubscribedToComedian, setUserSubscribedToComedian] = useState<
    boolean | null
  >(null);
  const [livestream, setLivestream] = useState<Livestream | null>(null);
  const [userPurchasedSpecial, setUserPurchasedSpecial] = useState<
    boolean | null
  >(null);

  const supabase = useMemo(() => createClientComponentClient<Database>(), []);

  const { subscriptionPrice } = useSubscriptionPriceForComedian(
    supabase,
    comedian.id
  );

  const { gate } = useFeatureGate("video_comments_stableid");
  // Explicitly check for true to ensure proper gating
  const isCommentsEnabled = gate?.value === true && gate?.value !== undefined;

  const [userFollowsComedian, setUserFollowsComedian] = useState(
    userFollowsComedianParam
  );

  const postAge = post.date ? dayjs().diff(dayjs(post.date), "hour") : 0;
  const specialUrl = `/${comedian.slug}/${special.slug}?autoplay=1`;
  const redirectTo = isLoggedOutState
    ? `/${comedian.slug}/${special.slug}?autoplay=1`
    : "";
  const isVerticalVideo = special.is_vertical === true;
  const isVerticalMobile =
    isMobileVideoLayoutEnabled && isVerticalVideo && isMobile;

  useEffect(() => {
    if (!comedian.about_img) return;
    setProfileImg(getCDNImageURL(comedian.about_img));
  }, [comedian.about_img]);

  useEffect(() => {
    let isCancelled = false;

    if (special.livestream) {
      getLivestreamById(supabase, special.livestream).then((livestream) => {
        if (!isCancelled) setLivestream(livestream);
      });
    }
    userHasPurchasedSpecial(supabase, special).then((purchased) => {
      if (!isCancelled) setUserPurchasedSpecial(purchased);
    });

    userIsSubscribedToComedian(supabase, comedian.id).then((subscribed) => {
      if (!isCancelled) setUserSubscribedToComedian(subscribed);
    });

    return () => {
      isCancelled = true;
    };
  }, [special, comedian.id, supabase]);

  useEffect(
    () => {
      let isCancelled = false;

      setVideoSrc(null);
      setIsLoadingToken(true);

      async function handlePreviewVideoURL() {
        if (special.preview_video_id) {
          const newPreviewUrl = await getPublicVideoURL(
            special.preview_video_id
          );
          if (!isCancelled) setPreviewUrl(newPreviewUrl);
        }
      }

      function handlePreviewImg() {
        if (special.preview_img) {
          const previewImg = getCDNImageURL(special.preview_img);
          setPreviewImg(previewImg);
        }
      }

      const handleVideoUrl = async () => {
        if (isLoggedOutState) {
          if (special.preview_video_id) {
            const videoSrc = await getPublicVideoURL(special.preview_video_id);
            if (!isCancelled) setVideoSrc(videoSrc);
          }
          if (!isCancelled) setIsLoadingToken(false);
        } else {
          // Guard: verify session is still active before making auth-dependent
          // call. During sign-out the component can remount with stale props
          // while the auth session is already cleared.
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session || isCancelled) {
            if (!isCancelled) setIsLoadingToken(false);
            return;
          }

          if (special.video_id) {
            const tokenRes = await getViewingTokenForSpecial(special.id);
            if (isCancelled) return;
            if (tokenRes === null) {
              toast({
                title: "Oops! Something went wrong, please try again.",
                variant: "destructive",
              });
            }
            setVideoSrc(tokenRes?.hasAccess ? tokenRes.token : null);
          }
          if (!isCancelled) setIsLoadingToken(false);
        }
      };

      handleVideoUrl();
      handlePreviewVideoURL();
      handlePreviewImg();

      return () => {
        isCancelled = true;
      };
    },
    // !Important! Don't remove userFollowsComedian from the dependency array.
    // It looks unused, but we need to re-fetch a video token when this state changes.
    [isLoggedOutState, special, userFollowsComedian, toast, supabase]
  );

  const posterImg = useMemo(
    () => (special.preview_img ? getCDNImageURL(special.preview_img) : ""),
    [special.preview_img]
  );

  const specialImg = useMemo(
    () =>
      getCDNImageURL(
        special.poster_img_with_titles || special.preview_img || ""
      ) || "",
    [special.poster_img_with_titles, special.preview_img]
  );

  const renderTimestamp = (postAge: number) => {
    return postAge < 24
      ? `${postAge}h ago`
      : postAge < 24 * 7
        ? `${Math.floor(postAge / 24)}d ago`
        : dayjs.utc(post.date).format("MMM D, YYYY");
  };

  const videoRefCallback = (element: HTMLDivElement) => {
    if (element) {
      // create an observer
      const observer = new IntersectionObserver(screenIntersectionCallback, {
        threshold: 0.1, // trigger callback when at least 10% of the element is visible in the viewport
      });
      // begin observing this element
      observer.observe(element);
      // clean up the observer when the component unmounts
      return () => {
        observer.disconnect();
      };
    }
  };

  const followCallback = async () => {
    setUserFollowsComedian(true);
  };

  // Show skeleton while loading
  if (!comedian || !profileImg || isLoadingToken || !specialImg) {
    return <VideoFeedCardSkeleton />;
  }

  const userHasAccessToSpecial = getUserCanAccessContent({
    special,
    userFollowsComedian,
    userPurchasedSpecial: userPurchasedSpecial ?? false,
    isUserSubscribed: userSubscribedToComedian ?? false,
  });

  // Common props for both components
  const commonProps = {
    post,
    special,
    comedian,
    userFollowsComedian,
    isLoggedOutState,
    muted,
    handleMutedChange,
    playing,
    videoRefCallback,
    videoSrc: videoSrc ?? "",
    profileImg,
    previewUrl,
    previewImg,
    userSubscribedToComedian,
    livestream,
    userPurchasedSpecial,
    subscriptionPrice,
    posterImg,
    specialImg,
    userHasAccessToSpecial,
    isCommentsEnabled,
    followCallback,
    renderTimestamp,
    postAge,
    specialUrl,
    redirectTo,
    hideEnabled,
    onHide,
  };

  // Render vertical component for mobile vertical videos
  if (isVerticalMobile) {
    return <VideoFeedCardVertical {...commonProps} />;
  }

  // Render horizontal component for all other cases
  return <VideoFeedCardHorizontal {...commonProps} />;
}

export default memo(VideoFeedCard);
